const cloneField = (field) => ({
  ...field,
  ...(Array.isArray(field.options) ? { options: [...field.options] } : {}),
});

export const DEFAULT_SESSION_OPTIONS = [
  'pre-market',
  'NY-AM',
  'NY-Lch',
  'NY-PM',
  'Asia',
  'London',
];

const DEFAULT_JOURNAL_FIELDS = [
  {
    id: 'tradeSession',
    label: 'Session',
    enabled: true,
    fieldType: 'dropdown',
    lockedType: true,
    placeholder: 'Select session',
    options: DEFAULT_SESSION_OPTIONS,
  },
  {
    id: 'economicRelease',
    label: 'Economic release',
    enabled: true,
    fieldType: 'dropdown',
    placeholder: 'Select news',
    options: [
      'No scheduled news',
      'CPI',
      'PPI',
      'NFP',
      'FOMC',
      'Unemployment Claims',
      'Other',
    ],
  },
  {
    id: 'pdArray',
    label: 'Entry model / PD array',
    enabled: true,
    // Free text (was a dropdown); locked so older saved settings can't turn it back.
    fieldType: 'text',
    lockedType: true,
    placeholder: 'e.g. FVG, order block',
  },
  {
    id: 'target',
    label: 'Target',
    enabled: true,
    fieldType: 'dropdown',
    placeholder: 'Select target',
    options: [
      'PDH',
      'PDL',
      'BSL',
      'SSL',
      'Daily high',
      'Daily low',
      'Session high',
      'Session low',
      'Equal highs',
      'Equal lows',
      'Other',
    ],
  },
  {
    id: 'rulesFollowed',
    label: 'Follow rules?',
    enabled: true,
    fieldType: 'yesNoNA',
    lockedType: true,
  },
];

export function getDefaultJournalFields() {
  return DEFAULT_JOURNAL_FIELDS.map(cloneField);
}

export function resolveJournalFields(data, fallbackFields = getDefaultJournalFields()) {
  const defaults = Array.isArray(fallbackFields) ? fallbackFields : getDefaultJournalFields();
  const savedFields = Array.isArray(data?.journalFields) ? data.journalFields : [];

  if (savedFields.length === 0) return defaults.map(cloneField);

  const defaultById = new Map(defaults.map((field) => [field.id, field]));
  const resolved = savedFields
    .filter((field) => field?.id && defaultById.has(field.id))
    .map((field) => {
      const base = defaultById.get(field.id);
      const merged = {
        ...base,
        ...field,
        id: base.id,
        lockedType: base.lockedType === true,
      };
      if (base.lockedType && merged.fieldType !== base.fieldType) {
        // An older saved type (e.g. the PD array dropdown) is replaced, with its placeholder.
        merged.fieldType = base.fieldType;
        merged.placeholder = base.placeholder;
      }
      if (merged.fieldType === 'dropdown') {
        merged.options = Array.isArray(field.options) ? [...field.options] : [...(base.options || [])];
      } else {
        delete merged.options;
      }
      return merged;
    });

  const seen = new Set(resolved.map((field) => field.id));
  defaults.forEach((field) => {
    if (!seen.has(field.id)) resolved.push(cloneField(field));
  });
  return resolved;
}

export function getJournalField(fields, fieldId) {
  const source = Array.isArray(fields) ? fields : getDefaultJournalFields();
  return source.find((field) => field.id === fieldId)
    || getDefaultJournalFields().find((field) => field.id === fieldId)
    || null;
}

export function getJournalFieldOptions(field, currentValue = '') {
  const options = [];
  const add = (value) => {
    const normalized = String(value || '').trim();
    if (!normalized || options.some((option) => option.toLowerCase() === normalized.toLowerCase())) return;
    options.push(normalized);
  };
  (field?.options || []).forEach(add);
  add(currentValue);
  return options;
}

export function cleanJournalFields(fields) {
  return (Array.isArray(fields) ? fields : getDefaultJournalFields()).map((field) => {
    const cleaned = cloneField(field);
    cleaned.label = String(cleaned.label || '').trim() || field.id;
    if (cleaned.fieldType === 'dropdown') {
      const seen = new Set();
      cleaned.options = (cleaned.options || [])
        .map((option) => String(option || '').trim())
        .filter((option) => {
          const key = option.toLowerCase();
          if (!option || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
    }
    return cleaned;
  });
}
